import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material'
import {
  Timeline,
  Speed,
  Assessment,
  CheckCircle,
  Warning,
  TrendingUp
} from '@mui/icons-material'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { AnalyticsService } from '@shared/services/ApiClient'
import { TicketAnalytics, AgentPerformance } from '@shared/types/entities'
import { useAuth } from '@shared/auth/AuthContext'
import { colors } from '@shared/styles/theme'
import { OrganizationFilter } from '../filters/OrganizationFilter'

// Chart colors
const PRIORITY_COLORS = {
  'Urgent': '#f44336',
  'High': '#ff9800',
  'Medium': '#2196f3',
  'Low': '#4caf50'
}

export function TicketAnalyticsView() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [analytics, setAnalytics] = useState<TicketAnalytics | null>(null)
  const [agentPerformance, setAgentPerformance] = useState<AgentPerformance[]>([])
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [childSiteFilter, setChildSiteFilter] = useState('')

  // Date filters (default last 30 days)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0]
  })

  useEffect(() => {
    loadAnalytics()
  }, [startDate, endDate, organizationFilter, childSiteFilter])

  const loadAnalytics = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await AnalyticsService.getTicketAnalytics(
        startDate,
        endDate,
        organizationFilter || undefined,
        childSiteFilter || undefined
      )
      setAnalytics(data)

      if (isAdmin) {
        const agentData = await AnalyticsService.getAgentPerformance(
          startDate,
          endDate,
          organizationFilter || undefined,
          childSiteFilter || undefined
        )
        setAgentPerformance(agentData)
      }
    } catch (err: any) {
      console.error('Failed to load analytics:', err)
      setError(err.message || 'Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const handleOrganizationChange = (orgId: string, siteId: string) => {
    setOrganizationFilter(orgId)
    setChildSiteFilter(siteId)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Analytics & Reports
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!analytics) {
    return null
  }

  // Prepare chart data
  const priorityData = Object.entries(analytics.ticketsByPriority).map(([name, value]) => ({
    name,
    value
  }))

  const statusData = Object.entries(analytics.ticketsByStatus).map(([name, value]) => ({
    name,
    value
  }))

  const categoryData = Object.entries(analytics.ticketsByCategory).map(([name, value]) => ({
    name,
    value
  }))

  const requesterData = Object.entries(analytics.ticketsByRequester).map(([name, value]) => ({
    name,
    value
  }))

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Analytics & Reports
      </Typography>

      {/* Date Range and Organization Filters */}
      <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <OrganizationFilter
          value={childSiteFilter || organizationFilter}
          onChange={handleOrganizationChange}
        />
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, color: colors.techBlue, mb: 1 }} />
              <Typography variant="h4">{analytics.totalTickets}</Typography>
              <Typography color="text.secondary">Total Tickets</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: colors.greenDark, mb: 1 }} />
              <Typography variant="h4">{analytics.slaComplianceRate.toFixed(1)}%</Typography>
              <Typography color="text.secondary">SLA Compliance</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Speed sx={{ fontSize: 40, color: colors.orange, mb: 1 }} />
              <Typography variant="h4">{analytics.averageResponseTimeHours.toFixed(1)}h</Typography>
              <Typography color="text.secondary">Avg Response Time</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Timeline sx={{ fontSize: 40, color: colors.blue, mb: 1 }} />
              <Typography variant="h4">{analytics.averageResolutionTimeHours.toFixed(1)}h</Typography>
              <Typography color="text.secondary">Avg Resolution Time</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: colors.greenDark, mb: 1 }} />
              <Typography variant="h4">{analytics.ticketsCreatedToday}</Typography>
              <Typography color="text.secondary">Created Today</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: colors.green, mb: 1 }} />
              <Typography variant="h4">{analytics.ticketsResolvedToday}</Typography>
              <Typography color="text.secondary">Resolved Today</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Warning sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
              <Typography variant="h4">{analytics.slaBreachedTickets}</Typography>
              <Typography color="text.secondary">SLA Breaches</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assessment sx={{ fontSize: 40, color: colors.techBlue, mb: 1 }} />
              <Typography variant="h4">{analytics.openTickets + analytics.inProgressTickets}</Typography>
              <Typography color="text.secondary">Active Tickets</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ticket Trend Chart */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            30-Day Ticket Trend
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.dailyTicketTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="created" stroke={colors.techBlue} name="Created" />
              <Line type="monotone" dataKey="resolved" stroke={colors.greenDark} name="Resolved" />
              <Line type="monotone" dataKey="closed" stroke="#9e9e9e" name="Closed" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Priority Distribution */}
        {priorityData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Priority Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={priorityData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {priorityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PRIORITY_COLORS[entry.name as keyof typeof PRIORITY_COLORS] || colors.blue} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Status Distribution */}
        {statusData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={statusData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill={colors.techBlue} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Category Distribution */}
        {categoryData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Category Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={categoryData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill={colors.orange} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Requester Distribution */}
        {requesterData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Requesters
                </Typography>
                <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                  Users creating the most tickets
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={requesterData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip />
                    <Bar dataKey="value" fill={colors.techBlue} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Agent Performance Table (Admin Only) */}
      {isAdmin && agentPerformance.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Agent Performance
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell align="right">Assigned</TableCell>
                    <TableCell align="right">Resolved</TableCell>
                    <TableCell align="right">Open</TableCell>
                    <TableCell align="right">Resolution Rate</TableCell>
                    <TableCell align="right">Avg Resolution Time</TableCell>
                    <TableCell align="right">SLA Breaches</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentPerformance.map((agent) => (
                    <TableRow key={agent.agentId}>
                      <TableCell>{agent.agentName}</TableCell>
                      <TableCell align="right">{agent.assignedTickets}</TableCell>
                      <TableCell align="right">{agent.resolvedTickets}</TableCell>
                      <TableCell align="right">{agent.openTickets}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${agent.resolutionRate.toFixed(1)}%`}
                          color={agent.resolutionRate >= 80 ? 'success' : agent.resolutionRate >= 60 ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{agent.averageResolutionTimeHours.toFixed(1)}h</TableCell>
                      <TableCell align="right">
                        {agent.slaBreaches > 0 ? (
                          <Chip label={agent.slaBreaches} color="error" size="small" />
                        ) : (
                          <Chip label="0" color="success" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

